import { Component, Vue } from "vue-property-decorator";
import "vue-loading-overlay/dist/vue-loading.css";
import { DocumentArchiveTranslations as TranslationModel} from "../models/documentArchive";
import * as ArchiveModels from "../models/documentArchive";
import http from "../services/Http";
const Loading = require("vue-loading-overlay");

@Component({ components: {
    Loading
}})
export default class DocumentArchive extends Vue {

    protected translations: TranslationModel;
    protected model: ArchiveModels.DocumentArchiveModel = {} as ArchiveModels.DocumentArchiveModel;
    protected mainFilterOption: ArchiveModels.DocumentFilterOption = {} as ArchiveModels.DocumentFilterOption;
    protected selectedFilters: ArchiveModels.DocumentFilterOption[]  = [];
    protected mainFilterPlaceHolder: ArchiveModels.DocumentFilterOption = {} as ArchiveModels.DocumentFilterOption;
    protected isLoading: boolean = false;
    private readonly apiUrl: string = "/api/documentarchive";
    private readonly url: string = "";
    private readonly urlParameterByFilterType: Map<ArchiveModels.DocumentFilterType, string> = new Map([
        [ ArchiveModels.DocumentFilterType.Group, "group" ],
        [ ArchiveModels.DocumentFilterType.Category, "category" ],
        [ ArchiveModels.DocumentFilterType.DocumentType, "type" ]
    ]);

    constructor() {
        super();
        this.polyfill();
        this.url = document.location.href;
        this.translations = window.__finja.translations as TranslationModel;
    }

    protected created() {
        this.initialize();
    }

    protected get downloadSectionHeading(): string | undefined {
        if (this.mainFilterOption.id) {
            return this.mainFilterOption.name;
        }
    }

    protected onMainFilterChange() {
        this.selectedFilters = [];
        this.search().then(this.updateUrl);
    }

    protected onFilterOptionChange(option: ArchiveModels.DocumentFilterOption) {

        if (option.filterType === ArchiveModels.DocumentFilterType.Group) {
            if (!this.selectedFilters.includes(option)) {
                // The group filter option was removed => clear all category filters
                this.selectedFilters = this.selectedFilters.filter(x => x.filterType === ArchiveModels.DocumentFilterType.Group);
            }
        }

        this.search().then(this.updateUrl);
    }

    private polyfill() {
        this.flatMap = typeof Array.prototype.flatMap === "function" ? this.flatMapNative : this.flatMapPolyfill;
    }

    private initialize() {
        let responsePromise: Promise<ArchiveModels.DocumentArchiveModel>;
        const urlRequest = this.getSearchModelFromUrl();

        if (urlRequest) {
            responsePromise = this.search(urlRequest)
                .then(response => {
                    const mainFilterOption = response.mainDocumentFilter.documentFilterOptions.find(x => x.id === urlRequest.mainFilterOption.id);

                    if (!mainFilterOption) {
                        throw new Error(`Filter option '${urlRequest.mainFilterOption.id}' does not exist!`);
                    }

                    this.mainFilterPlaceHolder.name = this.getMainFilterPlaceHolderName(response.mainDocumentFilter);
                    this.mainFilterOption = mainFilterOption;
                    const selectedFilters = urlRequest.selectedFilterOptions.map(x => x.id);
                    this.selectedFilters = this.flatMap<ArchiveModels.DocumentFilter, ArchiveModels.DocumentFilterOption>(response.documentFilters, x => x.documentFilterOptions)
                        .filter(y => selectedFilters.includes(y.id));
                    return response;
                });
        } else {
            responsePromise = http.get<ArchiveModels.DocumentArchiveModel>(this.apiUrl)
                .then(response => {
                    this.updateModel(response);
                    this.mainFilterPlaceHolder.name = this.getMainFilterPlaceHolderName(response.mainDocumentFilter);
                    this.mainFilterOption = this.mainFilterPlaceHolder;
                    return response;
                });
        }

        responsePromise.catch(error =>  {
            console.error(error);
        });
    }

    private getMainFilterPlaceHolderName(mainDocumentFilter: ArchiveModels.DocumentFilter): string {
        return this.translations.mainFilterSelectorPlaceHolder + " " + mainDocumentFilter.title;
    }

    private getSearchModelFromUrl(): ArchiveModels.DocumentSearchModel | null {
        const url = new URL(this.url);
        const documentType = url.searchParams.get(this.urlParameterByFilterType.get(ArchiveModels.DocumentFilterType.DocumentType) as string);

        if (!documentType) {
            return null;
        }

        const categoryList = url.searchParams.getAll(this.urlParameterByFilterType.get(ArchiveModels.DocumentFilterType.Category) as string);
        const categories = this.flatMap(categoryList, x => x.split(","));
        const groupList = url.searchParams.getAll(this.urlParameterByFilterType.get(ArchiveModels.DocumentFilterType.Group) as string);
        const groups = this.flatMap(groupList, x => x.split(","));
        const filters = categories.map(x => ({ id: x, filterType: ArchiveModels.DocumentFilterType.Category }))
            .concat(groups.map(x => ({ id: x, filterType: ArchiveModels.DocumentFilterType.Group })));

        return {
            mainFilterOption: {
                filterType: ArchiveModels.DocumentFilterType.DocumentType,
                id: documentType,
            },
            selectedFilterOptions: filters
        } as ArchiveModels.DocumentSearchModel;
    }

    /**
     * Performs a request to the api.
     */
    private search(request?: ArchiveModels.DocumentSearchModel): Promise<ArchiveModels.DocumentArchiveModel> {
        this.model.searchResult = [];
        this.isLoading = true;

        if (request === undefined) {
            request = {
                mainFilterOption: this.mainFilterOption,
                selectedFilterOptions: this.selectedFilters
            };
        }

        const promise = http.post<ArchiveModels.DocumentArchiveModel>(this.apiUrl, request)
            .then(data => {
                this.updateModel(data);
                return data;
            })
            .finally(() => {
                this.isLoading = false;
            });

        return promise;
    }

    private updateModel(data: ArchiveModels.DocumentArchiveModel) {
        this.model = data;
    }

    /**
     * Updates the URL in the browser address field to match the current search filter.
     */
    private updateUrl(): void {
        const url = new URL(this.url);

        if (this.mainFilterOption) {
            this.urlParameterByFilterType.forEach(x => url.searchParams.delete(x));
            url.searchParams.set(this.urlParameterByFilterType.get(ArchiveModels.DocumentFilterType.DocumentType) as string, this.mainFilterOption.id);
            this.selectedFilters
                .filter(filter => filter.filterType !== ArchiveModels.DocumentFilterType.DocumentType)
                .forEach(filter => {
                    url.searchParams.append(this.urlParameterByFilterType.get(filter.filterType) as string, filter.id);
                });
        }

        window.history.replaceState(null, "", url.toString());
    }

    private flatMap<T, K>(arr: T[], mapCallback: (arg: T) => K[]): K[] {
        throw new Error("Method not implemented.");
    }

    /**
     * Wrapper around Array.prototype.flatMap.
     * @param arr
     * @param mapCallback
     */
    private flatMapNative<T, K>(arr: T[], mapCallback: (arg: T) => K[]): K[] {
        return arr.flatMap(mapCallback);
    }

    /**
     * Polyfill for Array.prototype.flatMap. Executes Array.prototype.flatMap if it exists.
     * Returns a new array formed by applying a given callback function to each element of the array,
     * and then flattening the result by one level.
     * @param arr
     * @param mapCallback
     */
    private flatMapPolyfill<T, K>(arr: T[], mapCallback: (arg: T) => K[]): K[] {
        const map = arr.map(mapCallback);
        const flat = map.reduce((acc, val) => acc.concat(val), []);

        return flat;
    }
}
